import type {
  TalentProfile,
  WorkExperienceItem,
  EducationItem,
  CertificationItem,
} from "components/Screens/Marketplace/Customer/types";

export function mapTalentToOverviewItems(talent: TalentProfile) {
  const totalExperience = talent.overview?.totalYearsOfExperience || 0;
  const educationLevel =
    talent.overview?.highestEducation ||
    (talent.education && talent.education.length > 0 ? talent.education[0].level : "");

  const languages = (talent.overview?.languagesSpoken || []).join(", ");

  const certScoreNum = talent.overview?.certificationScore || 0;
  const certScore = certScoreNum > 0 ? `${certScoreNum}% percentile` : "";

  const items: { icon: string; label: string; value: string }[] = [];

  if (languages) {
    items.push({ icon: "languages-spoken", label: "Languages spoken", value: languages });
  }

  if (totalExperience > 0) {
    items.push({ icon: "work-experience", label: "Work experience", value: `${totalExperience}+ years` });
  }

  if (educationLevel) {
    items.push({ icon: "highest-education", label: "Highest education", value: educationLevel });
  }

  if (certScore) {
    items.push({ icon: "certification-score", label: "Certification score", value: certScore });
  }

  return items;
}

function parseDateOnly(value: string): Date {
  const [datePart] = String(value).split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1); // local midnight, no conversion
}

export function mapTalentToWorkExperience(talent: TalentProfile): WorkExperienceItem[] {
  return (talent.experiences || [])
    .filter((exp) => !!exp.startDate)
    .map((exp) => {
      const startDate = parseDateOnly(exp.startDate!);
      const endDate = exp.endDate ? parseDateOnly(exp.endDate) : null;

      const startMonth = startDate.toLocaleDateString("en-US", { month: "short" });
      const startYear = startDate.getFullYear();
      const dateRange = endDate
        ? `${startMonth} ${startYear} - ${endDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })}`
        : `${startMonth} ${startYear} - Present`;

      const rawAchievements = exp.achievements as string | string[] | undefined;
      let responsibilities: string[];
      if (Array.isArray(rawAchievements)) {
        responsibilities = rawAchievements;
      } else if (typeof rawAchievements === "string" && rawAchievements.trim()) {
        responsibilities = [rawAchievements];
      } else {
        responsibilities = [];
      }

      return {
        dateRange,
        jobTitle: exp.title || "Untitled",
        companyName: exp.company || "Unknown",
        isEdgeCustomer: !!exp.customerId,
        responsibilities,
      };
    });
}

export function mapTalentToEducation(talent: TalentProfile): EducationItem[] {
  return (talent.education || []).map((edu) => {
    const startYear = edu.startDate ? new Date(edu.startDate).getFullYear() : "";
    const endYear = edu.endDate ? new Date(edu.endDate).getFullYear() : "";
    let dateRange = "";
    if (startYear && endYear) dateRange = `${startYear} - ${endYear}`;
    else if (startYear) dateRange = `${startYear} - Present`;

    const degree =
      edu.title || (edu.level && edu.fieldOfStudy ? `${edu.level} of ${edu.fieldOfStudy}` : edu.level || "");

    return {
      dateRange,
      degree,
      institution: edu.institution || "Unknown",
    };
  });
}

export function mapTalentToSoftwareSkills(talent: TalentProfile) {
  return (talent.tools || []).map((tool) => ({
    name: tool.name,
    imageUrl: tool.logoUrl || "",
  }));
}

export function mapTalentToSkillsChips(talent: TalentProfile) {
  return (talent.skills || []).map((skill) => skill.name);
}

export function mapTalentToCertifications(talent: TalentProfile): CertificationItem[] {
  return (talent.certifications || []).map((cert) => ({
    title: cert.title || "Certification",
    issuedDate: cert.startDate || "Date not specified",
    isEdgeEdu: cert.isEdge || false,
  }));
}

export function generateBioFromTalent(talent: TalentProfile) {
  if (talent.bio) {
    return talent.bio;
  }
  return "";
}
